import React, { useState } from 'react'
import useSWR from 'swr'
import Select from 'react-select'
import CompetitionSelect, { CompetitionFilter } from '../CompetitionSelect'
import MatchSelect, { MATCH_SEARCH_HINT } from '../MatchSelect'
import {
  PageRecord, PageTypeInfo, api, fetchPageTypeConfig, fetchPages,
} from '../../services/api'
import formStyles from '../../styles/forms.module.scss'
import selectStyles from '../../styles/react-select.module.scss'

/**
 * A LinkRef names the page a link points at — never a raw path
 * (docs/html/tennischannel/pages-links.html, "Links"). This is the one
 * picker the spec calls for: choose a page from the site's catalog, plus
 * its subject for a per-entity page or the page row for a custom page.
 * Reuse it anywhere a link is authored — nav items, carousel view-more,
 * card/CTA links — so upgrading it (e.g. new subject pickers) upgrades
 * every surface at once.
 *
 * Surfaces that may point off-site (hero banner slides) opt into a raw-URL
 * escape hatch with `allowExternal`; the value then carries the href in
 * external_url under the EXTERNAL_PAGE_TYPE sentinel, and it is the host's
 * job to store that href where its metadata wants it (linkRefToInput refuses
 * the sentinel). Nav deliberately does not allow it.
 */
export interface LinkRefValue {
  /** Destination page type code; '' while unchosen. EXTERNAL_PAGE_TYPE when
   * the author chose a raw URL on a surface that allows one. */
  page_type: string
  /** Custom pages: the page row id. */
  page_id: string
  /** Dynamic (per-entity) pages: the subject id. */
  node_id: string
  /** External links: the raw href. Only meaningful under EXTERNAL_PAGE_TYPE. */
  external_url: string
}

export const EMPTY_LINK_REF: LinkRefValue = {
  page_type: '',
  page_id: '',
  node_id: '',
  external_url: '',
}

/** Sentinel page_type for a raw-URL link. Dunder-fenced so no real
 * page_type.code (word-character codes like 'tournament_detail') can
 * shadow it. */
export const EXTERNAL_PAGE_TYPE = '__external__'

export const isExternalLinkRef = (v: LinkRefValue): boolean =>
  v.page_type === EXTERNAL_PAGE_TYPE

/** A page type reached through a page row: no fixed path of its own, and not
 * per-entity. Derived rather than a hardcoded 'custom' so that a second
 * path-less type added to the page_type table works here too — the server
 * rule it mirrors (nav.py _validate_nav_target) is derived the same way. */
const needsPageId = (t: PageTypeInfo | undefined): t is PageTypeInfo =>
  !!t && !t.is_dynamic && t.path === null

/** Form value → API shape: empty strings become nulls, and fields that
 * don't apply to the chosen page type are dropped. Page LinkRefs only —
 * an external value has no page to reference, so hosts that allow one must
 * branch on isExternalLinkRef before calling this. */
export const linkRefToInput = (v: LinkRefValue, types: PageTypeInfo[]) => {
  if (isExternalLinkRef(v)) {
    throw new Error('external link has no LinkRef input — store external_url instead')
  }
  const pt = types.find(t => t.code === v.page_type)
  return {
    page_type: v.page_type,
    page_id: needsPageId(pt) && v.page_id ? v.page_id : null,
    node_id: pt?.is_dynamic && v.node_id.trim() ? v.node_id.trim() : null,
  }
}

/** Form value → the LinkRef shape stored inside widget metadata JSON.
 * Unlike linkRefToInput (nav's typed columns, where NULL is the empty value),
 * the metadata schema types every key as a string and forbids unknown ones —
 * so inapplicable keys are omitted rather than nulled, and the result is
 * exactly one of the two accepted forms. EXTERNAL_PAGE_TYPE is a form-only
 * sentinel and never reaches the stored JSON: an external link stores its
 * href alone. Returns null while incomplete, which callers store as
 * "no link". */
export const linkRefToMetadata = (
  v: LinkRefValue,
  types: PageTypeInfo[],
): Record<string, string> | null => {
  if (isExternalLinkRef(v)) {
    const url = v.external_url.trim()
    return url ? { external_url: url } : null
  }
  if (!v.page_type) return null
  const pt = types.find(t => t.code === v.page_type)
  const out: Record<string, string> = { page_type: v.page_type }
  if (needsPageId(pt) && v.page_id) out.page_id = v.page_id
  if (pt?.is_dynamic && v.node_id.trim()) out.node_id = v.node_id.trim()
  return out
}

/** Metadata LinkRef → form value, for reopening a saved widget. A stored
 * external_url comes back under the sentinel so the selector shows the
 * external option — the mirror of linkRefToMetadata. */
export const metadataToLinkRef = (
  raw: Record<string, unknown> | null | undefined,
): LinkRefValue => {
  const url = typeof raw?.external_url === 'string' ? raw.external_url : ''
  if (url) return { ...EMPTY_LINK_REF, page_type: EXTERNAL_PAGE_TYPE, external_url: url }
  return {
    page_type: typeof raw?.page_type === 'string' ? raw.page_type : '',
    page_id: typeof raw?.page_id === 'string' ? raw.page_id : '',
    node_id: typeof raw?.node_id === 'string' ? raw.node_id : '',
    external_url: '',
  }
}

/** Returns an error message, or null when the LinkRef is complete. */
export const validateLinkRef = (v: LinkRefValue, types: PageTypeInfo[]): string | null => {
  if (!v.page_type) return 'Choose a destination page type'
  if (isExternalLinkRef(v)) {
    const url = v.external_url.trim()
    if (!url) return 'Enter the link URL'
    // Absolute http(s) or site-relative. Anything else (javascript:, bare
    // "example.com") would be served to clients as-authored.
    if (!/^https?:\/\//.test(url) && !url.startsWith('/')) {
      return 'URL must start with https:// (or / for an on-site path)'
    }
    return null
  }
  const pt = types.find(t => t.code === v.page_type)
  if (needsPageId(pt) && !v.page_id) return `Choose a ${pt.title.toLowerCase()}`
  if (pt?.is_dynamic && !v.node_id.trim()) return `${pt.title} pages need a subject ID`
  return null
}

// Entity types whose subject is a tournament (slug.go's canonicalPath
// vocabulary uses 'competition'/'competition_parent' for them).
const TOURNAMENT_ENTITY_TYPES = new Set(['competition', 'competition_parent'])

// Entity types whose subject is a match. No seeded page type carries one yet
// (tennis_channel's playback page is entity_type 'video', which tc_slug can't
// resolve until its wiring lands), but ACE's pageEntityNodeTypes already
// resolves 'match' subjects, so the picker is ready when such a type appears.
const MATCH_ENTITY_TYPES = new Set(['match'])

// "A specific tournament…" for per-entity types (the ellipsis signals a
// follow-up picker); "Title (/path)" for static ones.
const pageTypeLabel = (t: PageTypeInfo): string =>
  t.is_dynamic
    ? `A specific ${t.title.toLowerCase()}…`
    : `${t.title}${t.path ? ` (${t.path})` : ''}`

/** Searchable tournament picker → node_id (tc_competition_parent_id).
 * Wraps the shared CompetitionSelect; the only extra work is resolving the
 * display name when editing an item picked in an earlier session, where the
 * stored LinkRef carries only the id. */
const TournamentPicker: React.FC<{
  value: string
  onChange: (id: string) => void
}> = ({ value, onChange }) => {
  const [picked, setPicked] = useState<CompetitionFilter | null>(null)

  const needsLookup = Boolean(value) && picked?.tc_id !== value
  const { data: detailsData } = useSWR(
    needsLookup ? ['competition-details', value] : null,
    () => api.get('/competitions/details', { parent_id: value }),
    { revalidateOnFocus: false }
  )
  const parent = detailsData?.parent_competition as
    | { tc_name?: string | null; ext_name?: string | null }
    | undefined
  const name = picked?.tc_id === value
    ? picked.name
    : parent?.tc_name ?? parent?.ext_name ?? '…'

  return (
    <CompetitionSelect
      value={value ? { tc_id: value, name } : null}
      onChange={selected => {
        const competition = selected as CompetitionFilter | null
        setPicked(competition)
        onChange(competition?.tc_id || '')
      }}
      placeholder="Search tournaments..."
    />
  )
}

interface LinkRefSelectorProps {
  siteId: string
  value: LinkRefValue
  onChange: (v: LinkRefValue) => void
  /** Offer a raw-URL destination alongside the page catalog. Only for
   * surfaces whose links may leave the site (hero banner slides); the
   * host stores the href itself — see linkRefToInput. */
  allowExternal?: boolean
}

/**
 * The destination fields render as sibling formStyles.fieldGroup blocks, so
 * the selector inherits whatever field spacing its host form uses.
 */
export const LinkRefSelector: React.FC<LinkRefSelectorProps> = ({
  siteId, value, onChange, allowExternal = false,
}) => {
  // Same SWR keys the merch pages use — deduped, so hosts that already
  // fetch these pay nothing extra.
  const { data: typeConfig } = useSWR(
    siteId ? ['page-type-config', siteId] : null,
    () => fetchPageTypeConfig(siteId),
    { revalidateOnFocus: false }
  )
  const types: PageTypeInfo[] = typeConfig?.types || []
  const selectedType = types.find(t => t.code === value.page_type)

  // Only the chosen type's page rows, and only for a type that needs them.
  const pageRowType = needsPageId(selectedType) ? value.page_type : null
  const { data: customPagesData } = useSWR(
    siteId && pageRowType ? ['merch-pages', siteId, pageRowType] : null,
    () => fetchPages(siteId, pageRowType!),
    { revalidateOnFocus: false }
  )
  const customPages: PageRecord[] = customPagesData?.pages || []

  const external = isExternalLinkRef(value)
  const options = [
    ...types.map(t => ({ value: t.code, label: pageTypeLabel(t) })),
    ...(allowExternal ? [{ value: EXTERNAL_PAGE_TYPE, label: 'External URL…' }] : []),
  ]

  return (
    <>
      <div className={formStyles.fieldGroup}>
        <label>{allowExternal ? 'Destination' : 'Page'}</label>
        <Select
          value={external
            ? { value: EXTERNAL_PAGE_TYPE, label: 'External URL…' }
            : selectedType
              ? { value: selectedType.code, label: pageTypeLabel(selectedType) }
              : null}
          onChange={opt => onChange({
            ...EMPTY_LINK_REF, page_type: opt?.value || '',
          })}
          options={options}
          placeholder="Choose a page type…"
          isSearchable
          className={selectStyles.reactSelect}
          classNamePrefix="react-select"
        />
      </div>
      {external && (
        <div className={formStyles.fieldGroup}>
          <label>URL</label>
          <input
            type="url"
            value={value.external_url}
            onChange={e => onChange({ ...value, external_url: e.target.value })}
            placeholder="https://example.com/promo"
          />
        </div>
      )}
      {needsPageId(selectedType) && (
        <div className={formStyles.fieldGroup}>
          <label>{selectedType.title}</label>
          <Select
            value={(() => {
              const p = customPages.find(p => p.id === value.page_id)
              return p ? { value: p.id, label: `${p.name} (${p.path})` } : null
            })()}
            onChange={opt => onChange({ ...value, page_id: opt?.value || '' })}
            options={customPages.map(p => ({ value: p.id, label: `${p.name} (${p.path})` }))}
            placeholder="Choose a page…"
            isSearchable
            className={selectStyles.reactSelect}
            classNamePrefix="react-select"
          />
        </div>
      )}
      {selectedType?.is_dynamic && (
        <div className={formStyles.fieldGroup}>
          <label>{selectedType.title}</label>
          {TOURNAMENT_ENTITY_TYPES.has(selectedType.entity_type || '') ? (
            <TournamentPicker
              value={value.node_id}
              onChange={node_id => onChange({ ...value, node_id })}
            />
          ) : MATCH_ENTITY_TYPES.has(selectedType.entity_type || '') ? (
            <>
              <MatchSelect
                value={value.node_id}
                onChange={node_id => onChange({ ...value, node_id })}
              />
              <p className={formStyles.hint}>{MATCH_SEARCH_HINT}</p>
            </>
          ) : (
            <input
              type="text"
              value={value.node_id}
              onChange={e => onChange({ ...value, node_id: e.target.value })}
              placeholder={`The ${selectedType.entity_type || 'entity'} this link points at`}
            />
          )}
        </div>
      )}
    </>
  )
}

export default LinkRefSelector
